import { getNatsClient } from '@khal-os/sdk/app';
import { useMemo } from 'react';
import { create } from 'zustand';
import { hasDesktopEntryPermission } from '../hooks/useLaunchApp';
import type { DesktopEntry } from '../types/desktop-entry';

/**
 * Identity remap for official Namastex apps. The kernel's `app_store` table
 * is seeded with names and icon paths that don't always match the launcher's
 * conventions — e.g. "File Manager" vs the launcher's "Files", or icon paths
 * like `/icons/folder.svg` that don't exist in the desktop's `public/` (the
 * Vite SPA fallback then serves them as `text/html`, which the browser
 * silently fails to decode and renders as a broken image).
 *
 * This map is the single source of truth for first-party app identity used by
 * BOTH the launcher (`mapRowToDesktopEntry` below) and the App Store
 * (`marketplace/AppCard.tsx`). Third-party apps (no entry here) still fall
 * through to the kernel-supplied values.
 */
export interface BuiltinDisplay {
	name: string;
	icon: string;
}

const EUGENIA_ICON =
	'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 64 64%22%3E%3Cdefs%3E%3ClinearGradient id=%22g%22 x1=%220%22 y1=%220%22 x2=%221%22 y2=%221%22%3E%3Cstop stop-color=%22%2314b8a6%22/%3E%3Cstop offset=%221%22 stop-color=%22%236366f1%22/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width=%2264%22 height=%2264%22 rx=%2216%22 fill=%22url(%23g)%22/%3E%3Cpath d=%22M18 36c8-18 20-18 28 0M22 36h24M27 25v22M37 25v22%22 fill=%22none%22 stroke=%22white%22 stroke-width=%224%22 stroke-linecap=%22round%22/%3E%3Ccircle cx=%2232%22 cy=%2234%22 r=%224%22 fill=%22white%22/%3E%3C/svg%3E';

export const BUILTIN_DISPLAY: Record<string, BuiltinDisplay> = {
	files: { name: 'Files', icon: '/icons/dusk/finder.svg' },
	settings: { name: 'Settings', icon: '/icons/dusk/system_preferences.svg' },
	'nats-viewer': { name: 'NATS Viewer', icon: '/icons/dusk/activity_monitor.svg' },
	'mission-control': { name: 'Mission Control', icon: '/icons/dusk/mission_control.svg' },
	ideas: { name: 'Ideas', icon: '/icons/dusk/notes.svg' },
	marketplace: { name: 'App Store', icon: '/icons/dusk/app_store.svg' },
	eugenia: { name: 'Eugenia', icon: EUGENIA_ICON },
	'eugenia-metrics': { name: 'Eugenia Metrics', icon: EUGENIA_ICON },
	'eugenia-metrics-pack': { name: 'Eugênia Metrics v2', icon: EUGENIA_ICON },
};

export const HIDDEN_APP_SLUGS = new Set(['genie', 'terminal']);

/** Resolve the canonical display name + icon for a slug. Returns undefined for third-party apps. */
export function resolveBuiltinDisplay(slug: string): BuiltinDisplay | undefined {
	return BUILTIN_DISPLAY[slug];
}


/**
 * Bundled apps — always visible regardless of NATS/PG state.
 * Everything else is installable via the marketplace.
 */
const BUNDLED_APPS: DesktopEntry[] = [
	{
		id: 'files',
		name: 'Files',
		icon: '/icons/dusk/finder.svg',
		exec: null,
		type: 'builtin',
		component: 'files',
		categories: ['System'],
		comment: 'File browser',
		onDesktop: true,
	},
	{
		id: 'settings',
		name: 'Settings',
		icon: '/icons/dusk/system_preferences.svg',
		exec: null,
		type: 'builtin',
		component: 'settings',
		categories: ['System'],
		comment: 'Desktop settings',
		onDesktop: true,
	},
	{
		id: 'marketplace',
		name: 'App Store',
		icon: '/icons/dusk/app_store.svg',
		exec: null,
		type: 'builtin',
		component: 'marketplace',
		categories: ['System'],
		comment: 'Browse and install apps',
		onDesktop: true,
	},
];

/** IDs of bundled apps — used to deduplicate when merging with installed apps and to test built-in renderer coverage. */
export const BUNDLED_APP_IDS = BUNDLED_APPS.map((a) => a.id);
const BUNDLED_IDS = new Set(BUNDLED_APP_IDS);

/** Shape of an installed app row joined with app_store from the NATS apps.list response. */
export interface InstalledAppRow {
	/** Kernel-supplied launchability verdict (core#app-launchability). Older
	 *  kernels won't send these — when undefined we treat the row as
	 *  launchable to preserve back-compat. */
	launchable?: boolean;
	launchMode?: 'frontend' | 'service' | 'backend' | 'unknown';
	runtimeKind?: 'image' | 'iframe' | 'v2-iframe';
	launchUrl?: string;
	origin?: string;
	launchEndpoint?: string;
	bundleAvailable?: boolean | null;
	unlaunchableReason?: 'missing-frontend-bundle';
	/** `installed_apps.id` (UUID) — required by AppSettingsPanel for envClient
	 *  targeting (`os.app.config.*` calls take installedAppId per Decision 9e
	 *  in the app-env-management wish). Optional in the type so older kernel
	 *  replies (pre-core#336) don't break parsing. */
	installedAppId?: string;
	slug: string;
	path: string;
	status: string;
	/** `installed_apps.lifecycle_state` enum — drives LifecycleBadge dots on
	 *  launcher icons + LifecycleBanner on open windows. Optional for the same
	 *  back-compat reason as `installedAppId`. */
	lifecycleState?: import('../types/lifecycle').LifecycleState;
	/** `installed_apps.manifest_json` — the pack's khal-app.json captured at
	 *  install time. Carries `env: []` schema for the Environment tab + any
	 *  install-time pack metadata. Optional / unknown shape; consumers narrow. */
	installedManifestJson?: unknown;
	store: {
		name: string;
		slug: string;
		iconUrl: string | null;
		iconLucide: string | null;
		shortDescription: string | null;
		category: string | null;
		manifestJson: unknown;
		permission: string | null;
		natsPrefix: string | null;
		defaultWidth: number | null;
		defaultHeight: number | null;
		fullSizeContent: boolean | null;
		minRole: string | null;
	} | null;
}

/** Extract bundleUrl from the app_store manifestJson if present. */
function extractBundleUrl(manifestJson: unknown): string | undefined {
	if (manifestJson && typeof manifestJson === 'object' && 'bundleUrl' in manifestJson) {
		const val = (manifestJson as Record<string, unknown>).bundleUrl;
		return typeof val === 'string' ? val : undefined;
	}
	return undefined;
}

function extractManifestId(manifestJson: unknown): string | undefined {
	if (manifestJson && typeof manifestJson === 'object' && 'id' in manifestJson) {
		const val = (manifestJson as Record<string, unknown>).id;
		return typeof val === 'string' && val.length > 0 ? val : undefined;
	}
	return undefined;
}

function extractStringField(manifestJson: unknown, field: 'runtimeKind' | 'launchUrl' | 'origin' | 'icon'): string | undefined {
	if (manifestJson && typeof manifestJson === 'object' && field in manifestJson) {
		const val = (manifestJson as Record<string, unknown>)[field];
		return typeof val === 'string' && val.length > 0 ? val : undefined;
	}
	return undefined;
}

function extractDesktopIcon(manifestJson: unknown): string | undefined {
	if (!manifestJson || typeof manifestJson !== 'object') return undefined;
	const desktop = (manifestJson as Record<string, unknown>).desktop;
	if (!desktop || typeof desktop !== 'object') return undefined;
	const icon = (desktop as Record<string, unknown>).icon;
	return typeof icon === 'string' && icon.length > 0 ? icon : undefined;
}

function extractIcon(row: InstalledAppRow, builtin: BuiltinDisplay | undefined): string {
	return (
		extractDesktopIcon(row.installedManifestJson) ??
		extractStringField(row.installedManifestJson, 'icon') ??
		extractDesktopIcon(row.store?.manifestJson) ??
		extractStringField(row.store?.manifestJson, 'icon') ??
		builtin?.icon ??
		row.store?.iconUrl ??
		row.store?.iconLucide ??
		'/icons/dusk/default.svg'
	);
}

function preferSpecificLaunchUrl(rowLaunchUrl: string | undefined, ...manifestLaunchUrls: Array<string | undefined>): string | undefined {
	const specificManifest = manifestLaunchUrls.find((candidate) => {
		if (!candidate) return false;
		try {
			const parsed = new URL(candidate);
			return parsed.pathname !== '/' || parsed.search.length > 0 || parsed.hash.length > 0;
		} catch {
			return false;
		}
	});
	if (!rowLaunchUrl) return specificManifest ?? manifestLaunchUrls.find(Boolean);
	try {
		const parsedRow = new URL(rowLaunchUrl);
		if (specificManifest && parsedRow.pathname === '/' && parsedRow.search.length === 0 && parsedRow.hash.length === 0) {
			return specificManifest;
		}
	} catch {
		// Keep legacy behavior for non-URL row values.
	}
	return rowLaunchUrl;
}

function normalizeRuntimeKind(value: unknown): 'image' | 'iframe' | undefined {
	// Older HML registry rows and early Runtime v2 manifests used
	// `v2-iframe`; the iframe renderer now uses the shorter canonical
	// `iframe` kind. Accept both so clean registry rows do not silently fall
	// back to the legacy bundle loader.
	if (value === 'v2-iframe') return 'iframe';
	return value === 'image' || value === 'iframe' ? value : undefined;
}

export function extractRuntimeKind(row: InstalledAppRow): 'image' | 'iframe' | undefined {
	const val = row.runtimeKind ?? extractStringField(row.installedManifestJson, 'runtimeKind') ?? extractStringField(row.store?.manifestJson, 'runtimeKind');
	return normalizeRuntimeKind(val);
}

function collectPermissionAliases(row: InstalledAppRow): string[] | undefined {
	const aliases = new Set<string>();
	for (const candidate of [
		row.store?.permission,
		row.store?.slug,
		extractManifestId(row.installedManifestJson),
		extractManifestId(row.store?.manifestJson),
	]) {
		if (typeof candidate !== 'string' || candidate.length === 0 || candidate === row.slug) continue;
		// Only trust slug-to-canonical aliases that match the launcher runtime
		// convention (`<canonical>-pack`). Arbitrary store/manifest metadata is
		// not proof that an unrelated dynamic slug should inherit that canonical
		// app's scoped permissions.
		if (row.slug === `${candidate}-pack`) aliases.add(candidate);
	}
	return aliases.size > 0 ? Array.from(aliases) : undefined;
}

/** Map a PG installed-app row to a DesktopEntry for the launcher. */
function mapRowToDesktopEntry(row: InstalledAppRow): DesktopEntry {
	const bundleUrl = extractBundleUrl(row.store?.manifestJson);
	const builtin = resolveBuiltinDisplay(row.slug);
	const runtimeKind = extractRuntimeKind(row);
	const launchUrl = preferSpecificLaunchUrl(
		row.launchUrl,
		extractStringField(row.installedManifestJson, 'launchUrl'),
		extractStringField(row.store?.manifestJson, 'launchUrl')
	);
	return {
		id: row.slug,
		name: builtin?.name ?? row.store?.name ?? row.slug,
		icon: extractIcon(row, builtin),
		exec: null,
		type: 'installed',
		component: row.slug,
		categories: row.store?.category ? [row.store.category] : ['System'],
		comment: row.store?.shortDescription ?? null,
		onDesktop: true,
		bundleUrl,
		permissionAliases: collectPermissionAliases(row),
		lifecycleState: row.lifecycleState,
		// Forward launchability verdict from the kernel — `useLaunchApp` reads
		// these to refuse opening a window for a known-broken installed app
		// (e.g. tickets / curadoria / agents-hub whose bundles aren't baked
		// into the HML image).
		launchable: row.launchable,
		launchMode: row.launchMode,
		unlaunchableReason: row.unlaunchableReason,
		runtimeKind,
		launchUrl,
		origin: row.origin ?? extractStringField(row.installedManifestJson, 'origin') ?? extractStringField(row.store?.manifestJson, 'origin'),
		launchEndpoint: row.launchEndpoint,
	};
}

/**
 * Per-row safe wrapper around `mapRowToDesktopEntry`. The kernel reply can
 * carry mixed shapes (built-in slugs use the manifest `id`, unstaged-disk
 * packs use the repo name as the slug, store metadata can be null, etc.).
 * One bad row must NEVER drop the entire launcher list — log it and skip.
 */
function safeMapRow(row: unknown): DesktopEntry | null {
	if (!row || typeof row !== 'object') {
		console.warn('[desktop-store] skipping non-object installed-app row', row);
		return null;
	}
	try {
		const r = row as InstalledAppRow;
		// A row without a slug cannot be addressed by the launcher / window
		// renderer (every lookup keys off `installedAppRowsBySlug[slug]`),
		// so drop it rather than ship a ghost entry with id=undefined.
		if (typeof r.slug !== 'string' || r.slug.length === 0) {
			console.warn('[desktop-store] skipping installed-app row with missing slug', row);
			return null;
		}
		return mapRowToDesktopEntry(r);
	} catch (err) {
		console.warn('[desktop-store] skipping installed-app row that failed to map', {
			row,
			error: err instanceof Error ? err.message : String(err),
		});
		return null;
	}
}

interface DesktopStore {
	apps: DesktopEntry[];
	desktopIcons: DesktopEntry[];
	wallpaper: string;
	pinnedApps: string[];
	loading: boolean;
	/** Set when fetchApps fails -- lets UI distinguish "loading" from "failed". */
	fetchError: string | null;
	/** Raw PG rows keyed by `slug`. Kept after fetchApps so AppSettingsPanel /
	 *  LifecycleBanner / WindowRenderer.getManifest can look up the installed-app
	 *  state (lifecycleState, manifest_json, installedAppId) at render time. */
	installedAppRowsBySlug: Record<string, InstalledAppRow>;

	setApps: (apps: DesktopEntry[]) => void;
	setWallpaper: (url: string) => void;
	pinApp: (appId: string) => void;
	unpinApp: (appId: string) => void;
	/** Fetch installed apps from PG via NATS. Falls back to static list on failure. */
	fetchApps: () => Promise<void>;
	/**
	 * Optional callback: called after fetchApps succeeds with the raw PG rows.
	 * Consumers (core) can register dynamic manifests and refresh role permissions.
	 */
	onAppsLoaded: ((rows: InstalledAppRow[]) => void) | null;
	setOnAppsLoaded: (cb: ((rows: InstalledAppRow[]) => void) | null) => void;
}

export const useDesktopStore = create<DesktopStore>((set, get) => ({
	apps: BUNDLED_APPS,
	desktopIcons: BUNDLED_APPS,
	wallpaper: '/wallpapers/default.svg',
	pinnedApps: [],
	loading: true,
	fetchError: null,
	onAppsLoaded: null,
	installedAppRowsBySlug: {},

	setApps: (apps) => set({ apps, desktopIcons: apps }),
	setWallpaper: (url) => set({ wallpaper: url }),
	pinApp: (appId) =>
		set((state) => ({
			pinnedApps: state.pinnedApps.includes(appId) ? state.pinnedApps : [...state.pinnedApps, appId],
		})),
	unpinApp: (appId) =>
		set((state) => ({
			pinnedApps: state.pinnedApps.filter((id) => id !== appId),
		})),

	setOnAppsLoaded: (cb) => set({ onAppsLoaded: cb }),

	fetchApps: async () => {
		try {
			const client = getNatsClient();
			const raw = await client.request('os.apps.list', undefined, 8000);
			const data = (typeof raw === 'string' ? JSON.parse(raw) : raw) as {
				apps?: unknown[];
				error?: string;
			};

			if (Array.isArray(data.apps) && data.apps.length > 0) {
				// Step 1: drop obviously broken rows (non-object, missing slug,
				// or status === 'error'). Use safeMapRow's slug check by deferring
				// validation to the map step below — we only filter `status` here.
				const accepted = data.apps.filter((r) => {
					try {
						if (!r || typeof r !== 'object') return false;
						const status = (r as { status?: unknown }).status;
						const slug = (r as { slug?: unknown }).slug;
						return status !== 'error' && !(typeof slug === 'string' && HIDDEN_APP_SLUGS.has(slug));
					} catch (filterErr) {
						console.warn(
							'[desktop-store] skipping row whose status/slug could not be read',
							filterErr instanceof Error ? filterErr.message : String(filterErr),
						);
						return false;
					}
				}) as InstalledAppRow[];

				// Step 2: index rows by slug for AppSettingsPanel / LifecycleBanner /
				// WindowRenderer.getManifest lookups. Skip rows without a slug —
				// they can never be addressed by the launcher anyway. Reading
				// `.slug` may throw on pathological rows (e.g. a getter that
				// blows up), so wrap each access in try/catch and just skip.
				const rowsBySlug: Record<string, InstalledAppRow> = {};
				for (const r of accepted) {
					try {
						if (typeof r.slug === 'string' && r.slug.length > 0) rowsBySlug[r.slug] = r;
					} catch (slugErr) {
						console.warn(
							'[desktop-store] skipping row whose slug could not be read',
							slugErr instanceof Error ? slugErr.message : String(slugErr),
						);
					}
				}

				// Step 3: notify consumer (core) of the validated rows so it can
				// register dynamic manifests and refresh role permissions.
				const callback = get().onAppsLoaded;
				if (callback) {
					try {
						callback(accepted);
					} catch (cbErr) {
						console.warn(
							'[desktop-store] onAppsLoaded callback threw; continuing',
							cbErr instanceof Error ? cbErr.message : String(cbErr),
						);
					}
				}

				// Step 4: map each installed row defensively. A malformed row
				// (id-format quirk, null store, missing bundleUrl, …) must NOT
				// drop the rest of the list — the launcher's job is to show
				// every app it can, not nothing. Even the `BUNDLED_IDS.has(slug)`
				// check is wrapped because reading `.slug` can throw on
				// pathological rows.
				const installedEntries: DesktopEntry[] = [];
				for (const r of accepted) {
					try {
						if (BUNDLED_IDS.has(r.slug)) continue;
					} catch {
						// Slug unreadable — let safeMapRow log + skip below.
					}
					const entry = safeMapRow(r);
					if (entry) installedEntries.push(entry);
				}

				// Step 5: merge — bundled apps are unconditional, installed
				// apps are appended. BUNDLED_APPS is a non-empty const so the
				// final list can never go empty, even if every installed row
				// fails to map.
				const merged = [...BUNDLED_APPS, ...installedEntries];
				set({
					apps: merged,
					desktopIcons: merged,
					installedAppRowsBySlug: rowsBySlug,
					loading: false,
					fetchError: null,
				});
				return;
			}
		} catch (err) {
			// NATS unavailable or service not running -- keep bundled apps visible
			set({ fetchError: `Failed to load apps: ${err instanceof Error ? err.message : String(err)}` });
		}
		// Empty / failed reply: make sure bundled apps are still visible. The
		// initial state already carries them, but if a prior fetch had set
		// `apps` to something else, restore the bundled baseline rather than
		// leaving stale data on the launcher.
		const current = get().apps;
		const hasAllBundled = BUNDLED_APPS.every((a) => current.some((c) => c.id === a.id));
		if (!hasAllBundled) {
			set({ apps: BUNDLED_APPS, desktopIcons: BUNDLED_APPS });
		}
		set({ loading: false });
	},
}));

/**
 * Returns desktop apps filtered by the given permissions array.
 *
 * Mirrors the same launcher predicate used by CommandPalette/useLaunchApp so
 * visibility and open decisions stay aligned.
 */
export function useFilteredDesktopApps(permissions: string[]): DesktopEntry[] {
	const desktopIcons = useDesktopStore((s) => s.desktopIcons);

	return useMemo(() => desktopIcons.filter((entry) => hasDesktopEntryPermission(entry, permissions)), [desktopIcons, permissions]);
}
